import './order-step.scss';

import { useContext, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import FormComments from '@/components/Shared/entities/form-comments';
import { ROUTES } from '@/components/Shared/routes/routes';
import { AdaptiveFormSteps, Button, FormSteps } from '@/components/Shared/ui';
import { useAppSelector } from '@/store';

import { CreateTabContext } from '../ExistedOrder';
import { REQUIRED_FIELDS } from './constants';
import useAutoCreditForm from './hooks/useAutoCreditForm';
import useCreateAutoCredit from './hooks/useCreateAutoCredit';
import useStepCount from './hooks/useStepCount';
import { FirstStep, SecondStep, ThirdStep } from './widgets';

const STEPS: React.ReactNode[] = [
  <FirstStep key="first-step" />,
  <SecondStep key="second-step" />,
  <ThirdStep key="third-step" />
];

type TOrderStepProps = {
  createRequest: (id: string) => void;
};

export const OrderStep = ({ createRequest }: TOrderStepProps) => {
  const {
    userAccesses: {
      autoCredit: { canWriteCommentInRequest }
    }
  } = useAppSelector((state) => state.userInfo);

  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);

  const { isFieldsFrozen } = useContext(CreateTabContext);
  const methods = useAutoCreditForm();
  const { stepCountItems } = useStepCount({ control: methods.control });

  const { createLoading, onSubmit } = useCreateAutoCredit({
    currentStep,
    setCurrentStep,
    trigger: methods.trigger,
    createRequest
  });

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  const modalCancelHandler = () => {
    setShowCancelModal(false);
    navigate(ROUTES.home);
  };

  const onCancelHandler = () => {
    setShowCancelModal(true);
  };

  const handleStep = async (val: number) => {
    if (currentStep === val) return;

    let isValid = true;
    if (!isFieldsFrozen && val > currentStep) {
      let iteration = -1;

      while (iteration < val && isValid) {
        iteration++;
        isValid =
          (await methods.trigger(REQUIRED_FIELDS[iteration])) ||
          iteration === val;
      }
      if (!isValid) {
        setCurrentStep(iteration);
        if (iteration < val) {
          setTimeout(() => {
            methods.trigger(REQUIRED_FIELDS[iteration]);
          }, 10);
        }
      }
    }

    if (val < currentStep) {
      methods.clearErrors();
      setCurrentStep(val);
    } else if (val > currentStep && isValid) {
      methods.clearErrors();
      setCurrentStep(val);
    }
  };

  return (
    <>
      <div className="order-step">
        <div className="order-step__side-container-adaptive">
          <AdaptiveFormSteps
            currentStep={currentStep + 1}
            steps={stepCountItems}
            onChange={handleStep}
          />
        </div>

        {canWriteCommentInRequest && (
          <div className="order-step__additional-actions-container-adaptive">
            <FormComments type="auto-credit" isAdaptive />
          </div>
        )}

        <FormProvider {...methods}>
          <form
            className="order-step__form"
            onSubmit={(e) => e.preventDefault()}
          >
            {STEPS.map((step, index) => (
              <div
                key={index}
                style={{ display: currentStep === index ? 'block' : 'none' }}
              >
                {step}
              </div>
            ))}

            {!isFieldsFrozen ? (
              <Button
                className="order-step__submit"
                variant="filled-red"
                disabled={Object.values(methods.formState.errors).length !== 0}
                isLoading={createLoading}
                onClick={() => onSubmit(methods.getValues())}
              >
                {currentStep !== 2 ? 'Продолжить' : 'Создать заявку'}
              </Button>
            ) : null}
          </form>
        </FormProvider>

        <div className="order-step__side-container">
          <FormSteps
            currentStep={currentStep + 1}
            steps={stepCountItems}
            onChange={handleStep}
            onCancel={!isFieldsFrozen ? onCancelHandler : undefined}
          />
          {canWriteCommentInRequest && <FormComments type="auto-credit" />}
        </div>
      </div>

      <CustomModal
        editClass="order-step-cancel"
        title="Вы хотите отменить создание заявки?"
        isModalOpen={showCancelModal}
        handleOk={modalCancelHandler}
        handleCancel={() => setShowCancelModal(false)}
        footer={[
          <Button key="back" onClick={() => setShowCancelModal(false)}>
            Нет
          </Button>,
          <Button
            variant="filled-red"
            key="submit"
            onClick={modalCancelHandler}
          >
            Да, отменить
          </Button>
        ]}
      >
        <p className="order-step-cancel__text">
          Внесенные данные не будут сохранены
        </p>
      </CustomModal>
    </>
  );
};
